import { ChangeEvent, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentError, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { DCUIThemingPreviewContainer } from "@containers/common/dcui-theming-preview-container/dcui-theming-preview-container";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIThemingErrorPageValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingErrorTabProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingErrorPageValidationErrors;
    dcuiTheme: DCUITheme;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingErrorTabContainer = (
    props: DCUIThemingErrorTabProps
) => {
    const {
        isDisabled,
        validationErrors,
        dcuiTheme,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        documentCaptureSteps,
        previewEntityType,
        updateDCUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const dcuiThemingResources =
        resources.pages.verificationServiceDetails.tabs.dcuiTheming;

    const updateDCUIContentErrorFieldValue = (
        key: keyof DCUIContentError,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            error: {
                ...dcuiContent.error,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        updateDCUIContentErrorFieldValue("header", newValue);
    };

    const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        updateDCUIContentErrorFieldValue("message", newValue);
    };

    const handleTransactionIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        updateDCUIContentErrorFieldValue("transactionId", newValue);
    };

    const handleResetTitle = () => {
        updateDCUIContentErrorFieldValue("header", inheritedDCUIContent.error.header);
    };

    const handleResetMessage = () => {
        updateDCUIContentErrorFieldValue("message", inheritedDCUIContent.error.message);
    };

    const handleResetTransactionId = () => {
        updateDCUIContentErrorFieldValue("transactionId", inheritedDCUIContent.error.transactionId);
    };

    return (
        <div>
            <ContentPageHeader title={dcuiThemingResources.subTabs.error.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={dcuiThemingResources.subTabs.error.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.error.header}
                            currentThemingPropertyValue={dcuiContent.error.header}
                            pageLevel={pageLevel}
                        >
                            <InputFormGroup
                                controlId={"document-capture-error-title-text-input"}
                                automationId={"document-capture-error-title-text-input"}
                                label={dcuiThemingResources.subTabs.error.inputs.title.label}
                                ariaLabel={dcuiThemingResources.subTabs.error.inputs.title.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleTitleChange}
                                value={dcuiContent.error.header || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.error.inputs.title.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetTitle}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.titleError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.error.message}
                            currentThemingPropertyValue={dcuiContent.error.message}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"document-capture-error-instructions-input"}
                                automationId={"document-capture-error-instructions-input"}
                                label={dcuiThemingResources.subTabs.error.inputs.instructions.label}
                                ariaLabel={dcuiThemingResources.subTabs.error.inputs.instructions.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleMessageChange}
                                value={dcuiContent.error.message || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.error.inputs.instructions.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.instructionsError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.error.transactionId}
                            currentThemingPropertyValue={dcuiContent.error.transactionId}
                            pageLevel={pageLevel}
                        >
                            <InputFormGroup
                                controlId={"document-capture-error-transaction-id-text-input"}
                                automationId={"document-capture-error-transaction-id-text-input"}
                                label={dcuiThemingResources.subTabs.error.inputs.transactionId.label}
                                ariaLabel={dcuiThemingResources.subTabs.error.inputs.transactionId.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleTransactionIdChange}
                                value={dcuiContent.error.transactionId || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.error.inputs.transactionId.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetTransactionId}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.transactionIdLabelError}
                    </ThemingFormContainer>

                    <DCUIThemingPreviewContainer
                        currentTab={DCUIThemingTabTypes.ERROR}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
